import PropTypes from 'prop-types';
import React, { memo } from 'react';
import classNames from 'classnames';

import style from './style.module.css';

const IconButtonComponent = ({
  icon,
  title,
  size = 'md',
  variant = 'default',
  active = false,
  className,
  href,
  ...rest
}) => {
  const commonProps = {
    ...rest,
    'aria-label': title,
    className: classNames(style.host, style[size], style[variant], { [style.active]: active }, className),
    title,
  };
  const glyph = <i aria-hidden="true" className={`fa fa-${icon}`} />;

  if (href) {
    return (
      <a {...commonProps} href={href} rel="noopener noreferrer" target="_blank">
        {glyph}
      </a>
    );
  }

  return (
    <button {...commonProps} type="button">
      {glyph}
    </button>
  );
};

IconButtonComponent.propTypes = {
  active: PropTypes.bool,
  className: PropTypes.string,
  href: PropTypes.string,
  icon: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['sm', 'md']),
  title: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'primary', 'success', 'danger']),
};

/**
 * Icon-only button: muted glyph that takes the accent color and a light tint on hover.
 *
 * @param props.icon Font Awesome 4 icon name without the `fa-` prefix, e.g. 'plus'.
 * @param props.title Tooltip and accessible label.
 * @param props.size 'md' — 28px hit area / 14px glyph; 'sm' — 24px / 12px for dense headers.
 * @param props.variant Accent color; 'success'/'danger' are tinted at rest; hover adds a background in the accent tint.
 * @param props.active Keeps the accent color at rest (selected state).
 * @param props.href When set, renders a link that opens in a new tab instead of a button.
 */
export const IconButton = memo(IconButtonComponent);
